import React, { useState, useEffect, useRef, useCallback } from "react";
import CustomFilesDropper from "../../components/dropers/CustomFilesDropper";
import { IoIosArrowDown } from "react-icons/io";
import Wrapper from "../../components/wrapper/Wrapper";
import { BsThreeDotsVertical } from "react-icons/bs";
import ImagePopUpModal from "../../components/Modal/ImagePopUpModal";
import SimplePagination from "../../components/Pagination/SimplePagination";
import { FiPlus, FiEdit3, FiShoppingCart, FiEye, FiX } from "react-icons/fi";
import {
  createMerchandise,
  deleteMerchandise,
  editMerchandise,
  getMerchandiseProducts,
} from "../../API/MerchandiseProductAPI/MerchandiseProductAPI";
import Loader from "../../components/Loader/Loader";
import toast from "react-hot-toast";
import AddSalesForm from "../../components/Form/AddSalesForm.jsx";
import { getAllMerchandiseCategories } from "../../API/MerchandiseCategoryAPI/MerchandiseCategoryAPI.js";

const MerchandiseProductPage = () => {
  const [allCategories, setAllCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    price: "",
    color: [],
    size: ["M"],
    categorie: [],
    productImage: [],
  });
  const [editingProduct, setEditingProduct] = useState(null);
  const [showModal, setShowModal] = useState({
    rowId: null,
  });
  const [showEditCreateModal, setShowEditCreateModal] = useState(false);
  const [showAddSalesModal, setShowAddSalesModal] = useState(null);
  const [addSaleFormData, setAddSaleFormData] = useState({
    user: null,
    totalItem: "",
    merchandiseProductId: "",
  });
  const [currentColor, setCurrentColor] = useState("#000000");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [fetchProductsLoading, setFetchProductsLoading] = useState(false);
  const [createEditLoading, setCreateEditLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [isCategoryOpen, setIsCategoryOpen] = useState(false);
  const [categorySearch, setCategorySearch] = useState("");
  const menuRef = useRef(null);

  const handleAddColor = () => {
    if (!formData.color.includes(currentColor)) {
      setFormData({
        ...formData,
        color: [...formData.color, currentColor],
      });
    }
  };

  const handleRemoveColor = (colorToRemove) => {
    setFormData({
      ...formData,
      color: formData.color.filter((color) => color !== colorToRemove),
    });
  };

  const handleSelectSize = (size) => {
    setFormData({
      ...formData,
      size: formData.size.includes(size)
        ? formData.size.filter((s) => s !== size)
        : [...formData.size, size],
    });
  };

  const handleSelectCategory = (category) => {
    setFormData({
      ...formData,
      categorie: formData.categorie.includes(category)
        ? formData.categorie.filter((cat) => cat !== category)
        : [...formData.categorie, category],
    });
  };

  const filteredCategories = allCategories.filter((category) =>
    category.name.toLowerCase().includes(categorySearch.toLowerCase())
  );

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const handleFileDrop = (filePaths, type) => {
    if (type === "image") {
      setFormData({ ...formData, productImage: filePaths || [] });
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    // Validate form data
    if (!formData.title.trim()) {
      toast.error("Product title is required");
      return;
    }
    if (!formData.description.trim()) {
      toast.error("Product description is required");
      return;
    }
    if (!formData.price || formData.price <= 0) {
      toast.error("Valid price is required");
      return;
    }
    if (formData.categorie.length === 0) {
      toast.error("At least one category is required");
      return;
    }
    if (formData.size.length === 0) {
      toast.error("At least one size is required");
      return;
    }

    if (editingProduct) {
      const editMerchandiseProduct = async () => {
        try {
          setCreateEditLoading(true);
          const editedProduct = await editMerchandise({
            ...formData,
            _id: editingProduct._id,
          });
          toast.success("Product updated successfully");
          setProducts((prev) => {
            let prevIs = prev.merchandiseProducts;
            const index = prevIs.findIndex(
              (product) => product._id === editedProduct._id
            );
            prevIs = [
              ...prevIs.slice(0, index),
              editedProduct,
              ...prevIs.slice(index + 1),
            ];
            return {
              ...prev,
              merchandiseProducts: prevIs,
            };
          });
          setShowEditCreateModal(false);
          setShowModal({ rowId: null });
          resetForm();
        } catch (error) {
          toast.error(
            error.response?.data?.message || "Error updating product"
          );
          console.error("Error updating product:", error);
        } finally {
          setCreateEditLoading(false);
        }
      };
      editMerchandiseProduct();
    } else {
      const handleCreateProduct = async () => {
        try {
          setCreateEditLoading(true);
          const createdProduct = await createMerchandise({
            productImage: formData.productImage,
            title: formData.title,
            categorie: formData.categorie,
            price: formData.price,
            description: formData.description,
            color: formData.color,
            size: formData.size,
          });
          setProducts((prev) => {
            let newProducts = [
              createdProduct,
              ...(prev?.merchandiseProducts || []),
            ];
            const startIndex = (page - 1) * limit;
            newProducts = newProducts.slice(startIndex, startIndex + limit);
            return { ...prev, merchandiseProducts: newProducts };
          });
          setShowEditCreateModal(false);
          toast.success("Product created successfully");
          resetForm();
        } catch (error) {
          toast.error(
            error.response?.data?.message || "Error creating product"
          );
          console.error("Error creating product:", error);
        } finally {
          setCreateEditLoading(false);
        }
      };
      handleCreateProduct();
    }
  };

  const handleEdit = (product) => {
    console.log("handleEdit called with product:", product);
    setEditingProduct(product);
    setFormData({
      _id: product._id,
      title: product.title,
      description: product.description,
      price: product.price,
      color: product.color,
      size: product.size,
      categorie: product.categorie,
      productImage: product.productImage || [],
    });
    setShowEditCreateModal(true);
    console.log("Edit modal should be open now");
  };

  const handleDelete = async (productId) => {
    try {
      setDeleteLoading(true);
      await deleteMerchandise({ _id: productId });
      toast.success("Product deleted successfully");
      const afterProducts = products.merchandiseProducts.filter(
        (product) => product._id !== productId
      );
      setProducts({
        ...products,
        merchandiseProducts: afterProducts,
      });
      // Close menu after successful deletion
      setShowModal({ rowId: null });
    } catch (error) {
      toast.error("Error deleting product");
      console.error(error);
      // Close menu even on error
      setShowModal({ rowId: null });
    } finally {
      setDeleteLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      _id: undefined,
      title: "",
      description: "",
      price: "",
      color: [],
      size: ["M"],
      categorie: [],
      productImage: [],
    });
    setEditingProduct(null);
    setCategorySearch("");
  };

  const showModalRef = useRef(showModal.rowId);
  useEffect(() => {
    showModalRef.current = showModal.rowId;
  }, [showModal.rowId]);

  const handleToggle = (rowId) => {
    setShowModal({ rowId });
  };

  // Close popup menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Only close if clicking outside the menu and not on any button
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        // Add a small delay to allow button clicks to register first
        setTimeout(() => {
          setShowModal({ rowId: null });
        }, 100);
      }
    };

    if (showModal.rowId) {
      document.addEventListener("click", handleClickOutside);
    }

    return () => {
      document.removeEventListener("click", handleClickOutside);
    };
  }, [showModal.rowId]);

  // Handle product row click to open edit dialog
  const handleProductClick = (product, event) => {
    // Don't trigger if clicking on the actions menu or any button
    if (
      event.target.closest("[data-actions-menu]") ||
      event.target.closest("button") ||
      event.target.closest("input") ||
      event.target.closest("select") ||
      event.target.closest("textarea")
    ) {
      return;
    }
    handleEdit(product);
  };

  const getMerchandiseProductsHandler = useCallback(async (page, limit) => {
    try {
      setFetchProductsLoading(true);
      getallCategories();
      const response = await getMerchandiseProducts({ page, limit });
      setProducts(response);
    } catch (error) {
      toast.error(error.response?.data?.message || "Error fetching products");
    } finally {
      setFetchProductsLoading(false);
    }
  }, []);

  useEffect(() => {
    getMerchandiseProductsHandler(page, limit);
  }, [page, limit, getMerchandiseProductsHandler]);

  async function getallCategories() {
    const response = await getAllMerchandiseCategories();
    setAllCategories(response.categories);
  }

  const handleLimitChange = (newLimit) => {
    setLimit(newLimit);
    setPage(1);
  };

  return (
    <Wrapper>
      {/* Header Section */}
      <div className="mb-8">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">
              Merchandise Products
            </h1>
            <p className="text-gray-600 mt-1">
              Manage your product catalog and inventory
            </p>
          </div>
          <button
            onClick={() => {
              setEditingProduct(null);
              setFormData({
                title: "",
                description: "",
                price: "",
                color: [],
                size: ["M"],
                categorie: [],
                productImage: [],
              });
              setShowEditCreateModal(true);
            }}
            className="inline-flex items-center gap-2 px-4 py-2 bg-[--primary] text-white rounded-lg hover:bg-[--primary]/90 transition-colors font-medium"
          >
            <FiPlus className="w-4 h-4" />
            Create Product
          </button>
        </div>
      </div>
      {/* Products Grid/Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        {!fetchProductsLoading && products?.merchandiseProducts?.length ? (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Product
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Details
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Price
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Variants
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Category
                  </th>
                  <th className="px-6 py-4 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {products?.merchandiseProducts?.map((product, index) => (
                  <tr
                    key={index}
                    className="hover:bg-gray-50 transition-colors cursor-pointer"
                    onClick={(event) => handleProductClick(product, event)}
                  >
                    {/* Product Column */}
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-4">
                        <div className="flex-shrink-0">
                          {product.productImage?.length > 0 ? (
                            <div className="flex gap-1">
                              <ImagePopUpModal
                                imageUrl={product.productImage[0]}
                                customClass="w-16 h-16 object-cover rounded-lg border border-gray-200"
                                alt={product.title}
                              />
                              {product.productImage.length > 1 && (
                                <div className="w-16 h-16 bg-gray-100 rounded-lg border border-gray-200 flex items-center justify-center text-xs text-gray-500">
                                  +{product.productImage.length - 1}
                                </div>
                              )}
                            </div>
                          ) : (
                            <div className="w-16 h-16 bg-gray-100 rounded-lg border border-gray-200 flex items-center justify-center">
                              <FiEye className="w-6 h-6 text-gray-400" />
                            </div>
                          )}
                        </div>
                        <div className="min-w-0 flex-1">
                          <h3 className="text-sm font-medium text-gray-900 truncate">
                            {product.title}
                          </h3>
                        </div>
                      </div>
                    </td>

                    {/* Details Column */}
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900">
                        <div className="text-gray-500 mt-1 line-clamp-2">
                          {product.description}
                        </div>
                      </div>
                    </td>

                    {/* Price Column */}
                    <td className="px-6 py-4">
                      <div className="text-sm font-semibold text-gray-900">
                        ${product.price}
                      </div>
                    </td>

                    {/* Variants Column */}
                    <td className="px-6 py-4">
                      <div className="space-y-2">
                        {/* Colors */}
                        {product.color?.length > 0 && (
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-gray-500">
                              Colors:
                            </span>
                            <div className="flex gap-1">
                              {product.color.slice(0, 3).map((color, idx) => (
                                <div
                                  key={idx}
                                  className="w-4 h-4 rounded-full border border-gray-300"
                                  style={{ backgroundColor: color }}
                                  title={color}
                                ></div>
                              ))}
                              {product.color.length > 3 && (
                                <span className="text-xs text-gray-500">
                                  +{product.color.length - 3}
                                </span>
                              )}
                            </div>
                          </div>
                        )}
                        {/* Sizes */}
                        {product.size?.length > 0 && (
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-gray-500">
                              Sizes:
                            </span>
                            <div className="flex gap-1 flex-wrap">
                              {product.size.slice(0, 4).map((size, idx) => (
                                <span
                                  key={idx}
                                  className="px-2 py-1 text-xs bg-gray-100 text-gray-700 rounded"
                                >
                                  {size}
                                </span>
                              ))}
                              {product.size.length > 4 && (
                                <span className="text-xs text-gray-500">
                                  +{product.size.length - 4}
                                </span>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    </td>

                    {/* Category Column */}
                    <td className="px-6 py-4">
                      <div className="flex flex-wrap gap-1">
                        {product.categorie?.slice(0, 2).map((category, idx) => (
                          <span
                            key={idx}
                            className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                          >
                            {category}
                          </span>
                        ))}
                        {product.categorie?.length > 2 && (
                          <span className="text-xs text-gray-500">
                            +{product.categorie.length - 2}
                          </span>
                        )}
                      </div>
                    </td>

                    {/* Actions Column */}
                    <td className="px-6 py-4 text-center" data-actions-menu>
                      <div className="relative" ref={menuRef}>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleToggle(product._id);
                          }}
                          className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                        >
                          <BsThreeDotsVertical className="w-4 h-4" />
                        </button>

                        {showModal.rowId === product._id && (
                          <div className="absolute right-0 top-8 bg-white border border-gray-200 shadow-lg rounded-lg py-1 z-10 min-w-[160px]">
                            <button
                              onMouseDown={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                console.log("Edit button clicked");
                                handleEdit(product);
                                setShowModal({ rowId: null });
                              }}
                              className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                            >
                              <FiEdit3 className="w-4 h-4" />
                              Edit
                            </button>
                            <button
                              onMouseDown={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                console.log(
                                  "Add Sales button clicked for product:",
                                  product._id
                                );
                                setShowAddSalesModal(product._id);
                                setAddSaleFormData((prev) => ({
                                  ...prev,
                                  merchandiseProductId: product._id,
                                }));
                                setShowModal({ rowId: null });
                              }}
                              className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                            >
                              <FiShoppingCart className="w-4 h-4" />
                              Add Sales
                            </button>
                            <div className="border-t border-gray-100 my-1"></div>
                            <button
                              onMouseDown={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                console.log("Delete button clicked");
                                handleDelete(product._id);
                                // Don't close menu immediately - let handleDelete close it after completion
                              }}
                              disabled={deleteLoading}
                              className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              {deleteLoading ? (
                                <>
                                  <div className="w-4 h-4 border-2 border-red-600 border-t-transparent rounded-full animate-spin"></div>
                                  Deleting...
                                </>
                              ) : (
                                "Delete"
                              )}
                            </button>
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <></>
        )}

        {/* Add Sales Modal - Render for each product */}
        {products?.merchandiseProducts?.map(
          (product) =>
            showAddSalesModal === product._id && (
              <AddSalesForm
                key={product._id}
                product={product}
                addSaleFormData={addSaleFormData}
                setAddSaleFormData={setAddSaleFormData}
                setShowAddSalesModal={setShowAddSalesModal}
              />
            )
        )}

        {/* Loading State */}
        {fetchProductsLoading && (
          <div className="flex justify-center items-center py-12">
            <Loader />
          </div>
        )}

        {/* Empty State */}
        {!fetchProductsLoading &&
          products?.merchandiseProducts?.length === 0 && (
            <div className="text-center py-12">
              <div className="mx-auto w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                <FiShoppingCart className="w-12 h-12 text-gray-400" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                No products found
              </h3>
              <p className="text-gray-500 mb-6">
                Get started by creating your first product.
              </p>
              <button
                onClick={() => {
                  setEditingProduct(null);
                  setFormData({
                    title: "",
                    description: "",
                    price: "",
                    color: [],
                    size: ["M"],
                    categorie: [],
                    productImage: [],
                  });
                  setShowEditCreateModal(true);
                }}
                className="inline-flex items-center gap-2 px-4 py-2 bg-[--primary] text-white rounded-lg hover:bg-[--primary]/90 transition-colors font-medium"
              >
                <FiPlus className="w-4 h-4" />
                Create Product
              </button>
            </div>
          )}
      </div>

      {/* Create/Edit Modal */}
      {showEditCreateModal && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex justify-center items-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-4xl max-h-[90vh] flex flex-col">
            {/* Modal Header */}
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-semibold text-gray-900">
                  {editingProduct ? "Edit Product" : "Create New Product"}
                </h2>
                <button
                  onClick={() => setShowEditCreateModal(false)}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <FiX className="w-6 h-6" />
                </button>
              </div>
            </div>

            {/* Modal Content */}
            <div className="flex-1 overflow-y-auto p-6">
              <form
                id="product-form"
                onSubmit={handleSubmit}
                className="space-y-6"
              >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Title */}
                  <div className="space-y-2">
                    <label
                      htmlFor="title"
                      className="block text-sm font-medium text-gray-700"
                    >
                      Product Title *
                    </label>
                    <input
                      type="text"
                      id="title"
                      name="title"
                      value={formData.title}
                      onChange={handleChange}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[--primary] focus:border-[--primary] transition-colors"
                      placeholder="Enter product title"
                      required
                    />
                  </div>

                  {/* Price */}
                  <div className="space-y-2">
                    <label
                      htmlFor="price"
                      className="block text-sm font-medium text-gray-700"
                    >
                      Price *
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <span className="text-gray-500 sm:text-sm">$</span>
                      </div>
                      <input
                        type="number"
                        id="price"
                        name="price"
                        value={formData.price}
                        onChange={handleChange}
                        className="w-full pl-7 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[--primary] focus:border-[--primary] transition-colors"
                        placeholder="0.00"
                        step="0.01"
                        min="0"
                        required
                      />
                    </div>
                  </div>
                </div>

                {/* Description */}
                <div className="space-y-2">
                  <label
                    htmlFor="description"
                    className="block text-sm font-medium text-gray-700"
                  >
                    Description *
                  </label>
                  <textarea
                    id="description"
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[--primary] focus:border-[--primary] transition-colors resize-none"
                    placeholder="Enter product description"
                    required
                  />
                </div>
                {/* Colors Section */}
                <div className="space-y-4">
                  <label className="block text-sm font-medium text-gray-700">
                    Product Colors
                  </label>
                  <div className="flex items-center gap-3">
                    <input
                      type="color"
                      value={currentColor}
                      onChange={(e) => setCurrentColor(e.target.value)}
                      className="w-12 h-12 border-2 border-gray-300 rounded-lg cursor-pointer"
                    />
                    <button
                      type="button"
                      onClick={handleAddColor}
                      className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors font-medium"
                    >
                      Add Color
                    </button>
                  </div>

                  {formData.color.length > 0 && (
                    <div className="space-y-2">
                      <p className="text-sm text-gray-600">Selected Colors:</p>
                      <div className="flex flex-wrap gap-2">
                        {formData.color.map((color, index) => (
                          <div
                            key={index}
                            className="flex items-center gap-2 bg-gray-50 rounded-lg px-3 py-2"
                          >
                            <div
                              style={{ backgroundColor: color }}
                              className="w-6 h-6 rounded-full border-2 border-white shadow-sm"
                            ></div>
                            <span className="text-sm text-gray-700">
                              {color}
                            </span>
                            <button
                              type="button"
                              onClick={() => handleRemoveColor(color)}
                              className="text-red-500 hover:text-red-700 transition-colors"
                            >
                              <FiX className="w-4 h-4" />
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
                {/* Sizes Section */}
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Available Sizes
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {["S", "M", "L", "XL", "XXL"].map((size) => (
                      <button
                        key={size}
                        type="button"
                        onClick={() => handleSelectSize(size)}
                        className={`px-4 py-2 rounded-lg border transition-colors ${
                          formData.size.includes(size)
                            ? "bg-[--primary] text-white border-[--primary]"
                            : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
                        }`}
                      >
                        {size}
                      </button>
                    ))}
                  </div>
                </div>
                {/* Categories Section */}
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Categories
                  </label>
                  <div
                    onClick={() => setIsCategoryOpen(!isCategoryOpen)}
                    className="w-full bg-white border border-gray-300 rounded-lg cursor-pointer focus:outline-none focus:ring-2 focus:ring-[--primary] focus:border-[--primary] transition-colors"
                  >
                    <div className="p-3 flex justify-between items-center">
                      <span className="text-gray-700">
                        {formData.categorie.length > 0
                          ? `${formData.categorie.length} categories selected`
                          : "Select categories"}
                      </span>
                      <IoIosArrowDown
                        className={`h-5 w-5 text-gray-400 transition-transform ${
                          isCategoryOpen ? "rotate-180" : ""
                        }`}
                      />
                    </div>

                    {isCategoryOpen && (
                      <div className="border-t border-gray-200">
                        <div className="p-3">
                          <input
                            type="text"
                            value={categorySearch}
                            onChange={(e) => setCategorySearch(e.target.value)}
                            placeholder="Search categories..."
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[--primary] focus:border-[--primary] transition-colors"
                          />
                        </div>
                        <div className="max-h-48 overflow-y-auto">
                          {filteredCategories.map((category) => (
                            <div
                              key={category._id}
                              className="flex items-center px-3 py-2 hover:bg-gray-50 cursor-pointer"
                              onClick={() =>
                                handleSelectCategory(category.name)
                              }
                            >
                              <input
                                type="checkbox"
                                checked={formData.categorie.includes(
                                  category.name
                                )}
                                onChange={() =>
                                  handleSelectCategory(category.name)
                                }
                                className="mr-3 h-4 w-4 text-[--primary] focus:ring-[--primary] border-gray-300 rounded"
                              />
                              <img
                                src={category.image}
                                alt={category.name}
                                className="w-8 h-8 rounded-lg mr-3 object-cover"
                              />
                              <span className="text-sm text-gray-700">
                                {category.name}
                              </span>
                            </div>
                          ))}
                          {filteredCategories.length === 0 && (
                            <div className="px-3 py-4 text-center text-gray-500 text-sm">
                              No categories found
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                {/* Product Images Section */}
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Product Images
                  </label>
                  <CustomFilesDropper
                    title="Drop or select product images"
                    instructions="Only image files (JPEG, PNG) - Max 5 files"
                    onFileDrop={handleFileDrop}
                    type="image"
                    fileTypes={["image/jpeg", "image/png"]}
                    defaultFiles={formData.productImage || []}
                    maxFiles={5}
                  />
                </div>
              </form>
            </div>

            {/* Modal Footer */}
            <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
              <button
                type="button"
                onClick={() => {
                  setShowEditCreateModal(false);
                  resetForm();
                }}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors font-medium"
              >
                Cancel
              </button>
              <button
                type="submit"
                form="product-form"
                disabled={createEditLoading}
                className="px-6 py-2 bg-[--primary] text-white rounded-lg hover:bg-[--primary]/90 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {createEditLoading ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    {editingProduct ? "Updating..." : "Creating..."}
                  </>
                ) : (
                  <>
                    {editingProduct ? (
                      <>
                        <FiEdit3 className="w-4 h-4" />
                        Update Product
                      </>
                    ) : (
                      <>
                        <FiPlus className="w-4 h-4" />
                        Create Product
                      </>
                    )}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      <SimplePagination
        totalPages={products?.paginationInfo?.totalPages || 1}
        currentPage={page}
        onPageChange={setPage}
        limit={limit}
        onLimitChange={handleLimitChange}
        totalItems={products?.paginationInfo?.totalItems || 0}
      />
    </Wrapper>
  );
};

export default MerchandiseProductPage;
